'use client';

import * as React from 'react';
import { Flex } from '../Flex';
import type { ComparisonTableProps, ComparisonColumn } from './types';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { ComparisonTableDesktop, ComparisonTableMobile } from './components';
import { useColumnViewObserver } from './hooks/useColumnViewObserver';
import { useIsDesktop } from '@/hen-ui/hooks';

/**
 * Column-driven comparison table following HEN Design System standards.
 *
 * Renders up to `maxItems` (default 3) `ComparisonItem` columns side by side
 * on desktop and as a 2-up swipeable carousel on mobile. Each row is declared
 * via a `ComparisonRowDef` whose `type` determines the cell renderer:
 * - `"text"` — plain text with a read-more/less toggle (`TextCell`); set
 *   `clamp: false` to show the full string, and `html: true` to render
 *   `text` as HTML (callers must sanitize upstream — hen-ui does not)
 * - `"badgeList"` — a wrapping list of `Badge` chips (`BadgeListCell`)
 * - `"dualText"` — current value, optional strikethrough, and optional badge (`DualTextCell`)
 *
 * Optional per-row label help uses the same `labelTooltip` /
 * `labelTooltipSide` / `labelTooltipFormat` / `labelTooltipTrigger`
 * contract as KPIBanner tiles — an info icon appears to the right of
 * the label when `labelTooltip` is set.
 *
 * Items beyond `maxItems` are silently truncated. An empty-slot column is
 * appended when `emptySlot` is provided and `items.length < maxItems`.
 *
 * Pass `setApi` to receive a `ComparisonTableApi` handle for external scroll
 * control (pagination buttons, dot indicators, etc.).
 *
 * @example
 * // Basic three-item comparison
 * <ComparisonTable
 *   items={programs}
 *   rowDefs={[
 *     { key: 'description', label: 'Description', type: 'text' },
 *     { key: 'attributes', label: 'Attributes', type: 'badgeList', badgeVariant: 'light' },
 *     { key: 'cost', label: 'Price', type: 'dualText' },
 *   ]}
 * />
 *
 * @example
 * // With an empty-slot column that invites the user to add another item
 * <ComparisonTable
 *   items={programs}
 *   rowDefs={rowDefs}
 *   emptySlot={{ label: 'Add a program', onClick: handleAdd, ariaLabel: 'Add a program to compare' }}
 * />
 *
 * @example
 * // Four items, two visible at a time, with external scroll controls
 * const [api, setApi] = React.useState<ComparisonTableApi | null>(null);
 *
 * <ComparisonTable items={programs} rowDefs={rowDefs} maxItems={4} visibleItems={2} setApi={setApi} />
 * <CarouselPrevious disabled={!api?.canScrollPrev} onClick={() => api?.scrollPrev()} />
 * <CarouselNext disabled={!api?.canScrollNext} onClick={() => api?.scrollNext()} />
 */
export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  items,
  rowDefs,
  emptySlot,
  maxItems = 3,
  visibleItems = 3,
  setApi,
  styleVersion = 'v2',
  infiniteScroll = false,
  inView = false,
  onProductViewed,
  onProductClicked,
  onCtaClick,
  scrollRegionAriaLabel,
  ...props
}) => {
  const isDesktop = useIsDesktop();

  // Each breakpoint has its own visible-items count. Only the active one
  // should fire onProductViewed — calling the hook once here (rather than
  // inside each variant) means the wrong breakpoint's window size can never
  // mark columns as viewed before the correct one runs.
  const firedRef = React.useRef<Set<string>>(new Set());

  // Limit to maxItems
  const safeItems = React.useMemo(
    () => items.slice(0, maxItems),
    [items, maxItems],
  );

  const shouldShowEmptySlot = !!emptySlot && safeItems.length < maxItems;

  // Build columns with optional empty slot
  const columns = React.useMemo<ComparisonColumn[]>(() => {
    if (!shouldShowEmptySlot || !emptySlot) {
      return safeItems;
    }

    return [
      ...safeItems,
      {
        id: 'empty-slot',
        kind: 'empty',
        label: emptySlot.label,
        onClick: emptySlot.onClick,
        ariaLabel: emptySlot.ariaLabel,
      },
    ];
  }, [emptySlot, safeItems, shouldShowEmptySlot]);

  // Desktop shows `visibleItems` columns; mobile always shows 2.
  // Using `isDesktop` ensures we only track views for the columns that are
  // actually visible in the rendered variant — the CSS-hidden variant is
  // excluded from view tracking entirely.
  const activeVisibleItems = isDesktop ? visibleItems : 2;

  // currentPage is managed inside each variant's scroll hook. We proxy it up
  // via a shared ref so the single hook call here always sees the live value.
  const currentPageRef = React.useRef(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const onPageChange = React.useCallback((page: number) => {
    currentPageRef.current = page;
    setCurrentPage(page);
  }, []);

  useColumnViewObserver(
    columns,
    activeVisibleItems,
    currentPage,
    inView,
    firedRef,
    (column) => onProductViewed?.(column.id),
  );

  return (
    <Flex
      direction='column'
      gap='lg'
      {...props}
      data-hen-id={COMPONENT_IDS.ComparisonTable}
    >
      <ComparisonTableDesktop
        columns={columns}
        rowDefs={rowDefs}
        visibleItems={visibleItems}
        setApi={setApi}
        inView={inView}
        onPageChange={onPageChange}
        onProductClicked={onProductClicked}
        onCtaClick={onCtaClick}
        scrollRegionAriaLabel={scrollRegionAriaLabel}
      />
      <ComparisonTableMobile
        columns={columns}
        rowDefs={rowDefs}
        safeItems={safeItems}
        styleVersion={styleVersion}
        infiniteScroll={infiniteScroll}
        inView={inView}
        onPageChange={onPageChange}
        onProductClicked={onProductClicked}
        onCtaClick={onCtaClick}
        scrollRegionAriaLabel={scrollRegionAriaLabel}
      />
    </Flex>
  );
};

ComparisonTable.displayName = 'ComparisonTable';
